import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Button from './ui/Button';

const HeroSection: React.FC = () => {
  const { translations } = useLanguage();
  const navigate = useNavigate();

  const bannerButtons = [
    { key: 'cosmetic', label: translations.hero.banner.buttons.cosmetic },
    { key: 'pediatric', label: translations.hero.banner.buttons.pediatric },
    { key: 'general', label: translations.hero.banner.buttons.general },
    { key: 'whitening', label: translations.hero.banner.buttons.whitening },
  ];

  const handleBookClick = (treatment?: string) => {
    const bookingPath = treatment
      ? `/kontakt?booking=true&treatment=${treatment}`
      : '/kontakt?booking=true';
    navigate(bookingPath);

    // Smooth scroll to booking form if already on kontakt page
    const element = document.getElementById('booking-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center text-white pt-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-navy opacity-75 z-0"
          style={{
            backgroundImage: "url('/images/SeaDent Palma Teaser Header.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay'
          }}
          aria-hidden="true"
        ></div>

        {/* Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/50 to-transparent z-1 pointer-events-none"></div>

        <div className="container-custom relative z-10 py-12">
          <div className="max-w-4xl mx-auto text-center animate-fadeIn">
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 text-white text-shadow-lg leading-tight font-title animate-slideIn">
              {translations.hero.title}
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-white opacity-95 text-shadow-md font-light tracking-wide max-w-2xl mx-auto">
              {translations.hero.subtitle}
            </p>
            {(translations.hero.teaser || translations.hero.description) && (
              <p className="mb-10 text-lg text-white opacity-80 text-shadow-md hidden md:block max-w-2xl mx-auto">
                {translations.hero.teaser || translations.hero.description}
              </p>
            )}
            <div className="animate-scaleUp animation-delay-300">
              <Button
                onClick={() => handleBookClick()}
                variant="coral"
                size="xl"
                className="shadow-premium hover:shadow-coral/50 transform hover:scale-105 transition-all text-lg font-bold tracking-wide"
                rightIcon={
                  <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                }
              >
                {translations.hero.cta}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* New Modern Banner Section */}
      <section className="bg-white py-16 md:py-24 border-b border-gray-100 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.5" className="text-navy" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-left space-y-8">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy leading-tight font-title">
                {translations.hero.banner.title.split(',').map((part, i) => (
                  <span key={i} className="block">
                    {part.trim()}
                    {i === 0 && <span className="text-mint text-6xl">.</span>}
                  </span>
                ))}
              </h2>
              <div className="h-1 w-20 bg-mint rounded-full"></div>
              <p className="text-xl text-gray-500 font-medium italic leading-relaxed">
                {translations.hero.banner.subtitle}
              </p>
            </div>

            <div className="space-y-4">
              {bannerButtons.map((btn, index) => (
                <div
                  key={btn.key}
                  className="flex rounded-xl overflow-hidden shadow-soft hover:shadow-premium transition-all transform hover:-translate-y-1 group cursor-pointer border border-gray-100 min-h-[80px] bg-white relative"
                  onClick={() => handleBookClick(btn.key)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Left: Treatment Name */}
                  <div className="flex-grow bg-white px-5 md:px-8 py-3 flex items-center group-hover:bg-cream transition-colors">
                    <span className="text-base md:text-xl font-bold text-navy group-hover:text-navy-light transition-colors leading-tight">
                      {btn.label}
                    </span>
                  </div>

                  {/* Right: Persistently Visible Book Button 
                      Mobile: Fixed width (130px) to fit "JETZT BUCHEN" / "Записаться".
                      Desktop: Larger width.
                  */}
                  <div className="bg-navy text-white flex items-center justify-center font-bold tracking-wider uppercase transition-all duration-300 w-[130px] md:w-[200px] flex-shrink-0 group-hover:bg-mint group-hover:text-navy">
                    <span className="flex items-center justify-center text-[10px] md:text-sm whitespace-nowrap px-1">
                      {translations.common.bookNow || 'Book'}
                      <svg className="w-3 h-3 md:w-4 md:h-4 ml-1.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
